import { isNotNullable } from "effect/Predicate";
import { drawTree, Forest, makeTree } from "./tree-formatter";

export interface BaseErrorParams {
  message?: string;
  cause?: unknown;
  meta?: Record<string, unknown>;
}

function makeForest(cause: unknown, level = 1): Forest<string> {
  if (cause instanceof Error) {
    return [
      makeTree(
        `(🔗 Cause ${level}): ${String(cause)}`,
        makeForest(cause.cause, level + 1)
      ),
    ];
  }

  return [];
}

export abstract class BaseError extends Error {
  public readonly meta?: Record<string, unknown>;

  constructor({ message = "", cause, meta }: BaseErrorParams = {}) {
    const messageWithMeta = (message += meta
      ? `\n\nMeta:\n${JSON.stringify(meta, null, 2)}`
      : "");

    const tree = makeTree(messageWithMeta ?? "", makeForest(cause));

    const messageWithCauses = drawTree(tree);

    super(messageWithCauses, { cause });
    this.name = this.constructor.name;

    if (isNotNullable(meta)) this.meta = meta;
  }
}

export class ScriptError extends BaseError {
  public readonly _tag = "ScriptError";
}

export interface BaseServerParams extends BaseErrorParams {
  status?: number;
  code?: number | string;
}

export abstract class BaseServerError extends BaseError {
  public readonly code?: number | string;
  public readonly status?: number;

  constructor({ status, code, ...rest }: BaseServerParams) {
    super(rest);

    if (isNotNullable(code)) this.code = code;
    if (isNotNullable(status)) this.status = status;
  }
}

export class ServerError extends BaseServerError {
  public readonly _tag = "ServerError";
}

export interface BaseClientParams extends BaseErrorParams {
  status?: number;
  code?: number | string;
}

export abstract class BaseClientError extends BaseError {
  public readonly code?: number | string;
  public readonly status?: number;

  constructor({ status, code, ...rest }: BaseClientParams) {
    super(rest);

    if (isNotNullable(code)) this.code = code;
    if (isNotNullable(status)) this.status = status;
  }
}

export class ClientError extends BaseClientError {
  public readonly _tag = "ClientError";
}

export class RateLimitError extends BaseClientError {
  public readonly _tag = "RateLimitError";
}

export class NotFoundError extends BaseClientError {
  public readonly _tag = "NotFoundError";
}
